import { Box, Collapsible, Heading, Text } from "@chakra-ui/react";
import { forwardRef, ReactNode } from "react";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { Button } from "./button";
import { Card } from "./card";
import { PreHeader } from "./pre-header";

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  /**
   * Optional Geist Mono UPPERCASE micro-label rendered above the title — the
   * editorial pre-header pattern used throughout the design system to anchor
   * a section visually ("Display", "Theme", "Portal"). Omit to render the
   * title alone.
   */
  pre?: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode;
  /**
   * Optional footer slot. When provided, renders a hairline-separated row
   * below the body — used for "Preview / Save" or "Save User Interface
   * Settings" action rows that belong to a specific section.
   */
  footer?: ReactNode;
  defaultOpen?: boolean;
  contentProps?: React.ComponentProps<typeof Box>;
  rootProps?: Omit<React.ComponentProps<typeof Card.Root>, "children">;
  bodyProps?: Omit<React.ComponentProps<typeof Card.Body>, "children">;
  contentRootProps?: Omit<
    React.ComponentProps<typeof Collapsible.Content>,
    "children"
  >;
}

export const CollapsibleSection = forwardRef<
  HTMLDivElement,
  CollapsibleSectionProps
>(function CollapsibleSection(
  {
    title,
    description,
    pre,
    isOpen,
    onOpenChange,
    children,
    footer,
    defaultOpen = true,
    contentProps,
    rootProps,
    bodyProps,
    contentRootProps,
  },
  ref,
) {
  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={(e: any) => onOpenChange(e.open)}
    >
      <Card.Root {...rootProps}>
        <Card.Body p={3} {...bodyProps}>
          <Collapsible.Trigger asChild>
            <Button
              variant="ghost"
              w="full"
              justifyContent="space-between"
              px={2}
              py={1.5}
              _hover={{ bg: "var(--bg-hover)" }}
              borderRadius="md"
              height="auto"
            >
              <Box textAlign="left">
                {pre && (
                  <Box mb={1.5}>
                    <PreHeader size="xs">{pre}</PreHeader>
                  </Box>
                )}
                <Heading size="md" color="var(--text-primary)">
                  {title}
                </Heading>
                {description && (
                  <Text
                    fontSize="xs"
                    color="var(--text-secondary-color)"
                    mt={0.5}
                  >
                    {description}
                  </Text>
                )}
              </Box>
              {isOpen ? <MdExpandLess size="20" /> : <MdExpandMore size="20" />}
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content {...contentRootProps}>
            <Box mt={3} px={2} {...contentProps}>
              {children}
            </Box>
            {footer && (
              <Box
                mt={4}
                pt={4}
                px={2}
                borderTopWidth="1px"
                borderColor="var(--border-default)"
                display="flex"
                justifyContent="flex-end"
                gap={2}
              >
                {footer}
              </Box>
            )}
          </Collapsible.Content>
        </Card.Body>
      </Card.Root>
    </Collapsible.Root>
  );
});
